import { applicantSection, feeInfo, feeProofAttachment, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B13. Wniosek o wydanie lokalnej karty mieszkańca lub karty seniora — program gminy (uchwała rady na podstawie
 * art. 7 ust. 1 i art. 18 ust. 2 pkt 15 usg + regulamin i wzór wniosku w zarządzeniu wójta). Nie jest to postępowanie
 * w trybie KPA (bez decyzji i odwołania). Brak wzoru ogólnopolskiego; układ jak wzór Kobyłki (zał. 2 do regulaminu)
 * pobrany 2026-10-22 — https://www.kobylka.pl/Common/pobierzPlik/id/1459/module_short/port/obj_id/3888/culture/pl/version/pub.html
 * (dla kogo karta, dane dziecka, zakres wniosku: pierwsza karta / przedłużenie / kolejna karta / aktualizacja danych,
 * fotografia, oświadczenia, dokumenty PIT). Podstawa uprawnienia steruje załącznikami (katalog: zameldowanie, PIT
 * w gminie, emeryt/rencista, rolnik, uczeń/student bez dochodu, osoba bez dochodu). Oświadczenia bez klauzuli art. 233
 * KK (regulamin gminy jej nie ustanowi — art. 233 § 6 KK wymaga ustawy) i bez zgód RODO (podstawą przetwarzania jest
 * zadanie gminy). Bez sekcji pełnomocnika — rodzic składa wniosek za dziecko jako przedstawiciel ustawowy. Lista
 * dokumentów i opłata za duplikat zależą od regulaminu programu — do potwierdzenia z wydziałem.
 */
const NOT_RENEWAL: EbiCondition = { not: { field: "zakresWniosku", equals: "przedluzenie" } };
const DUPLICATE: EbiCondition = { field: "zakresWniosku", equals: "duplikat" };
const CHILDREN: EbiCondition = { field: "kartaDla", in: ["dzieci", "siebieIDzieci"] };
const BASIS = (value: string): EbiCondition => ({ field: "podstawa", equals: value });

const B13: IEbiFormDefinition = {
  code: "B13",
  version: 1,
  title: "Wniosek o wydanie karty mieszkańca lub karty seniora",
  category: EEbiFormCategory.Community,
  office: EEbiFormOffice.Municipality,
  description: "Lokalna karta mieszkańca albo karta seniora (zniżki w gminnych instytucjach, komunikacji i u partnerów programu).",
  keywords: ["karta mieszkańca", "karta seniora", "karta miejska", "zniżki", "program lojalnościowy", "senior"],
  organ: "Wójt / Burmistrz / Prezydent Miasta jako organizator programu (albo operator wskazany w regulaminie)",
  legalBasis:
    "art. 7 ust. 1 i art. 18 ust. 2 pkt 15 ustawy z dnia 8 marca 1990 r. o samorządzie gminnym; uchwała rady gminy w sprawie programu karty i regulamin programu",
  request:
    "Wnoszę o wydanie karty (przedłużenie jej ważności, wydanie kolejnej karty albo aktualizację danych) na zasadach regulaminu programu — w zakresie wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Pierwsza karta bez opłaty; za wydanie kolejnej karty (duplikatu) — opłata według regulaminu programu. Opłata skarbowa nie występuje.",
  deadlineLabel: "wg regulaminu",
  deadline: "Według regulaminu programu — zwykle do 30 dni.",
  sections: [
    applicantSection(),
    {
      code: "wniosek",
      title: "Wniosek dotyczy",
      fields: [
        {
          code: "rodzajKarty",
          type: "radio",
          label: "Karta",
          required: true,
          defaultValue: "mieszkanca",
          options: [
            { value: "mieszkanca", label: "karta mieszkańca" },
            { value: "seniora", label: "karta seniora" },
          ],
        },
        {
          code: "zakresWniosku",
          type: "radio",
          label: "Wnioskuję o",
          required: true,
          defaultValue: "wydanie",
          options: [
            { value: "wydanie", label: "wydanie pierwszej karty" },
            { value: "przedluzenie", label: "przedłużenie terminu ważności karty" },
            { value: "duplikat", label: "wydanie kolejnej karty (duplikatu)" },
            { value: "aktualizacja", label: "aktualizację danych" },
          ],
        },
        { code: "uzasadnienieDuplikatu", type: "textarea", label: "Uzasadnienie wydania kolejnej karty (np. utrata, zniszczenie, zmiana nazwiska)", required: true, visibleWhen: DUPLICATE, maxLength: 1000 },
        {
          code: "zakresAktualizacji",
          type: "text",
          label: "Aktualizacja danych w zakresie",
          required: true,
          visibleWhen: { field: "zakresWniosku", equals: "aktualizacja" },
          maxLength: 300,
        },
        {
          code: "kartaDla",
          type: "radio",
          label: "Karta ma być wydana",
          required: true,
          defaultValue: "siebie",
          options: [
            { value: "siebie", label: "dla mnie" },
            { value: "dzieci", label: "dla mojego niepełnoletniego dziecka (dzieci)" },
            { value: "siebieIDzieci", label: "dla mnie i mojego niepełnoletniego dziecka (dzieci)" },
          ],
        },
        {
          code: "dzieci",
          type: "repeat",
          label: "Dzieci, dla których ma być wydana karta",
          itemLabel: "Dziecko",
          required: true,
          visibleWhen: CHILDREN,
          min: 1,
          max: 10,
          fields: [
            // kody inne niż pola wnioskodawcy — PESEL wnioskodawcy (pole `pesel`) BE porównuje z mObywatelem
            { code: "imieDziecka", type: "text", label: "Imię (imiona)", required: true, width: "half", maxLength: 100 },
            { code: "nazwiskoDziecka", type: "text", label: "Nazwisko", required: true, width: "half", maxLength: 100 },
            { code: "peselDziecka", type: "pesel", label: "PESEL", required: true, width: "half" },
          ],
        },
      ],
    },
    {
      code: "uprawnienie",
      title: "Podstawa uprawnienia do karty",
      fields: [
        {
          code: "podstawa",
          type: "radio",
          label: "Uprawnienie do karty wynika z",
          required: true,
          hint: "Wybór określa dokumenty, które trzeba dołączyć — szczegóły w regulaminie programu.",
          options: [
            { value: "zameldowanie", label: "zameldowania na pobyt stały na terenie gminy (urząd sprawdzi w rejestrze mieszkańców)" },
            { value: "pit", label: "rozliczania podatku dochodowego (PIT) ze wskazaniem gminy jako miejsca zamieszkania" },
            { value: "emeryt", label: "pobierania emerytury lub renty — PIT-40A albo PIT-11A z ZUS lub KRUS z adresem w gminie" },
            { value: "rolnik", label: "prowadzenia gospodarstwa rolnego na terenie gminy" },
            { value: "uczen", label: "nauki lub studiów bez własnego dochodu — na podstawie uprawnienia rodzica (opiekuna prawnego)" },
            { value: "bezDochodu", label: "zamieszkiwania w gminie bez dochodu (opłata za odpady w gminie, świadczenia OPS albo rejestracja w PUP)" },
          ],
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        { code: "oswZamieszkanie", type: "declaration", label: "Oświadczam, że zamieszkuję na terenie gminy.", required: true },
        {
          code: "oswDane",
          type: "declaration",
          label: "Oświadczam, że podane we wniosku dane osobowe (moje i dzieci) są pełne i prawdziwe; zobowiązuję się niezwłocznie poinformować o ich zmianie.",
          required: true,
        },
        {
          code: "oswRodzic",
          type: "declaration",
          label: "Oświadczam, że jestem rodzicem (opiekunem prawnym) dzieci wskazanych we wniosku, a załączone fotografie są aktualne i przedstawiają te dzieci.",
          required: true,
          visibleWhen: CHILDREN,
        },
        {
          code: "oswRegulamin",
          type: "declaration",
          label: "Oświadczam, że zapoznałam/zapoznałem się z regulaminem programu karty i zobowiązuję się do jego przestrzegania.",
          required: true,
        },
        feeInfo(
          "infoOdbior",
          "Kartę odbiera się osobiście w punkcie wydawania kart po okazaniu dokumentu tożsamości ze zdjęciem; inna osoba może ją odebrać na podstawie upoważnienia, jeśli dopuszcza to regulamin programu.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "fotografia",
      label: "Aktualna fotografia każdej osoby, dla której ma być wydana karta",
      hint: "Plik JPG lub PNG (do 5 MB), proporcje jak zdjęcie 35×45 mm, jednolite jasne tło, twarz na wprost (ok. 2/3 kadru). Nie dotyczy przedłużenia ważności.",
      visibleWhen: NOT_RENEWAL,
      required: true,
      multiple: true,
    },
    {
      code: "pit",
      label: "Pierwsza strona zeznania PIT za ubiegły rok z UPO albo z pieczęcią wpływu urzędu skarbowego, albo zaświadczenie z urzędu skarbowego",
      hint: "Wystarczy pierwsza strona zeznania (z adresem w gminie) — z urzędowym poświadczeniem odbioru (UPO), prezentatą urzędu skarbowego albo dowodem nadania pocztą; zamiast niej zaświadczenie urzędu skarbowego o rozliczaniu podatku z adresem w gminie.",
      visibleWhen: BASIS("pit"),
      required: true,
    },
    {
      code: "pitEmeryt",
      label: "Pierwsza strona PIT-40A albo PIT-11A z ZUS lub KRUS za ostatni rok",
      visibleWhen: BASIS("emeryt"),
      required: true,
    },
    {
      code: "rolnik",
      label: "Decyzja o wymiarze podatku rolnego (nakaz płatniczy) oraz potwierdzenie ubezpieczenia w KRUS albo dowód opłaty za gospodarowanie odpadami",
      visibleWhen: BASIS("rolnik"),
      required: true,
    },
    {
      code: "legitymacja",
      label: "Legitymacja szkolna albo studencka",
      visibleWhen: BASIS("uczen"),
      required: true,
    },
    {
      code: "oswiadczenieRodzica",
      label: "Podpisane oświadczenie rodzica (opiekuna prawnego) wraz z dokumentem potwierdzającym jego uprawnienie (np. pierwsza strona PIT z UPO)",
      hint: "Uprawnienie ucznia lub studenta bez dochodu wynika z uprawnienia rodzica — EBI nie podpisze jego oświadczenia, dołącz skan.",
      visibleWhen: BASIS("uczen"),
      required: true,
    },
    {
      code: "bezDochodu",
      label: "Oświadczenie o wnoszeniu opłaty za gospodarowanie odpadami w gminie albo decyzja lub zaświadczenie OPS, albo zaświadczenie z urzędu pracy",
      visibleWhen: BASIS("bezDochodu"),
      required: true,
    },
    feeProofAttachment("Dowód uiszczenia opłaty za wydanie kolejnej karty", {
      required: false,
      visibleWhen: DUPLICATE,
      hint: "Gdy regulamin programu przewiduje opłatę za kolejną kartę — potwierdzenie przelewu na rachunek urzędu.",
    }),
    otherAttachments(),
  ],
};

export default B13;
